import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { StorageService } from '../../../common/services/storage.service';
import { CurrentUser } from '../../../common/models/user';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CreateAllocation, UpdateAllocation } from '../../../common/models/allocation';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Guid } from 'guid-typescript';
import { BudgetPlanComponent } from './budget/budget.component';
import { Association } from '../../../common/models/association';
import { AssociationService } from '../../../common/services/association.service';
import { AllocationService } from '../../../common/services/allocation.service';
import { FileUploadComponent } from '../../../common/components/file/file.component';
import { FormattedNumberInputDirective } from '../../../common/directives/inputs/formatted-number.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BudgetPlan } from '../../../common/models/submission';
import { DatetimeComponent } from '../../../common/components/datetime/datetime.component';
import { catchError, EMPTY, finalize, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CostType, FundingSourceType } from '../../../common/models/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';

@Component({
  selector: 'app-allocation',
  templateUrl: './allocation.component.html',
  styleUrls: ['./allocation.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    BudgetPlanComponent,
    FileUploadComponent,
    DatetimeComponent,
    FormattedNumberInputDirective
]
})
export class AllocationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
  };
  
  isHandset: boolean = false;

  isLoading = true;
  isUpdating = false;
  isSaving = false;

  preliminaryStepCompleted: boolean = false;
  allocationId!: string;

  user: CurrentUser | null = null;

  generalInfoFormGroup: FormGroup;
  activityInfoGroup: FormGroup;
  justificationFormGroup: FormGroup;
  budgetPlanFormGroup: FormGroup;
  attachmentsFormGroup: FormGroup;

  constructor(
    private _formBuilder: FormBuilder,
    private associationService: AssociationService,
    private allocationService: AllocationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private breakpointObserver: BreakpointObserver
  ) {

    this.generalInfoFormGroup = this._formBuilder.group({
      organizationName: [null],
      address: [null],
      postalCodeAndHeadquarters: [null],
      county: [null],
      registrationNumber: [null],
      oib: [null],
      rno: [null],
      telephone: [null],
      fax: [null],
      organizationEmail: [null],
      website: [null],
      authorizedRepresentative: [null],
      bankName: [null],
      bankAccountNumber: [null]
    });

    this.activityInfoGroup = this._formBuilder.group({
      activityName: [null],
      targetGroups: [null],
      amountRequestedFromMunicipality: [null],
      implementationStart: [null],
      implementationEnd: [null],
      activityDescription: [null],
      fieldOfOperation: [null],
      territorialCoverage: [null],
      volunteersInvolved: [false],
      potentialVolunteerCount: [null],
      projectLeader: [null]
    });

    this.justificationFormGroup = this._formBuilder.group({
      justification: [null]
    });

    this.budgetPlanFormGroup = this._formBuilder.group({
      id: [''],
      directCosts: this._formBuilder.array([]),
      indirectCosts: this._formBuilder.array([]),
      fundingSources: this._formBuilder.array([])
    });

    this.attachmentsFormGroup = this._formBuilder.group({
      fileStorageLocation: [`allocations/${Guid.create().toString()}`]
    });
  }

  ngOnInit(): void {

    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isHandset = result.matches;
      });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params.get('allocationId')) {
        this.allocationId = params.get('allocationId')!;

        this.isLoading = true;
        this.isUpdating = true;
        
         this.loadAllocationData$().pipe(
          finalize(() => this.isLoading = false),
          takeUntil(this.destroy$)
        ).subscribe();

      } else {
        this.initializeBudgetPlan();
        this.isLoading = false;
        this.isUpdating = false;
      }
    });

    this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      if (!this.isUpdating && this.user) {
        // We are in create mode and user is now available

        // Patch what we can directly from the user first
        this.patchFieldsFromUser();

        // If user has an association, fetch association details
        if (this.user.associationId) {
          this.associationService.getAssociation(this.user.associationId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (association) => {
              this.patchFieldsFromAssociation(association);
            },
            error: (err) => {
              console.error('Error fetching association data', err);
            }
          });
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeBudgetPlan(): void {
    this.populateBudgetPlanForm();
  }
    
  private populateBudgetPlanForm(budgetPlan?: BudgetPlan | null): void {
      const fb = this._formBuilder;
  
      const defaultFunding = [
        { id: '', type: FundingSourceType.OwnFunds, amount: 0 },
        { id: '', type: FundingSourceType.OtherPublicAuthorities, amount: 0 },
        { id: '', type: FundingSourceType.LocalAndRegionalAuthorities, amount: 0 },
        { id: '', type: FundingSourceType.Other, amount: 0 }
      ];
  
     const funding = (budgetPlan?.fundingSources?.length ? budgetPlan.fundingSources : defaultFunding).map(s => fb.group({
        id: [s.id ?? ''],
        type: [s.type, Validators.required],
        amount: [Number(s.amount ?? 0), [Validators.required, Validators.min(0)]]
      }));
  
      const items = budgetPlan?.budgetItems ?? [];
  
      const itemGroup = (i: any) => fb.group({
        id: [i.id ?? ''],
        type: [i.type, Validators.required],
        description: [i.description ?? '', [Validators.required, Validators.maxLength(500)]],
        amountRequested: [Number(i.amountRequested ?? 0), [Validators.required, Validators.min(0)]],
        totalAmount: [Number(i.totalAmount ?? 0), [Validators.required, Validators.min(0)]],
        explanation: [i.explanation ?? '', [Validators.required, Validators.maxLength(1000)]],
        fileStorageLocation: [i.fileStorageLocation || `budgetitems/${Guid.create().toString()}`]
      });
  
      const directCosts = items.filter(i => i.type === CostType.Direct).map(itemGroup);
      const indirectCosts = items.filter(i => i.type === CostType.Indirect).map(itemGroup);
  
      // Set arrays in one shot (avoids intermediate valueChanges noise)
      this.budgetPlanFormGroup.setControl('fundingSources', fb.array(funding));
      this.budgetPlanFormGroup.setControl('directCosts', fb.array(directCosts));
      this.budgetPlanFormGroup.setControl('indirectCosts', fb.array(indirectCosts));
  
      // Set id without emitting changes
      this.budgetPlanFormGroup.patchValue(
        { id: budgetPlan?.id ?? '' },
        { emitEvent: false }
      );
  }

  private patchFieldsFromUser(): void {
    if (!this.user) return;

    this.generalInfoFormGroup.patchValue({
      authorizedRepresentative: `${this.user.firstName} ${this.user.lastName}`,
      authorizedRepresentativeEmail: this.user.email,
      authorizedRepresentativeRole: this.user.title
    });
  }

  private patchFieldsFromAssociation(association: Association): void {

    this.generalInfoFormGroup.patchValue({
      organizationName: association.name,
      address: association.address,
      postalCodeAndHeadquarters: association.postalCodeAndHeadquarters,
      county: association.county,
      registrationNumber: association.registrationNumber,
      oib: association.oib,
      rno: association.rno,
      telephone: association.contactNumber,
      organizationEmail: association.email,
      website: association.web,
      bankName: association.bankName,
      bankAccountNumber: association.bankAccountNumber
    });
  }

  private loadAllocationData$(): Observable<any> {
    if (!this.allocationId) {
      return EMPTY;
    }

    return this.allocationService.getAllocation(Guid.parse(this.allocationId)).pipe(
      tap(allocation => {
        this.generalInfoFormGroup.patchValue(allocation);
        this.activityInfoGroup.patchValue(allocation);
        this.justificationFormGroup.patchValue(allocation);
        this.attachmentsFormGroup.patchValue(allocation);
        this.populateBudgetPlanForm(allocation.budgetPlan);
      }),
      catchError(error => {
        console.error('Error loading allocation:', error);
        return EMPTY;
      })
    );
  }

  private buildBudgetPlan(): BudgetPlan {
    const raw = this.budgetPlanFormGroup.getRawValue() as any;

    const budgetItemsDirect = (raw?.directCosts ?? []).map((i: any) => ({
      id: i?.id ? Guid.parse(i.id).toString() : Guid.EMPTY.toString(),
      type: i?.type,
      description: i?.description ?? '',
      amountRequested: i?.amountRequested ?? 0,
      totalAmount: i?.totalAmount ?? 0,
      explanation: i?.explanation ?? '',
      fileStorageLocation: i?.fileStorageLocation
    }));

    const budgetItemsIndirect = (raw?.indirectCosts ?? []).map((i: any) => ({
      id: i?.id ? Guid.parse(i.id).toString() : Guid.EMPTY.toString(),
      type: i?.type,
      description: i?.description ?? '',
      amountRequested: i?.amountRequested ?? 0,
      totalAmount: i?.totalAmount ?? 0,
      explanation: i?.explanation ?? '',
      fileStorageLocation: i?.fileStorageLocation
    }));

    const fundingSources = (raw?.fundingSources ?? []).map((s: any) => ({
      id: s?.id ? Guid.parse(s.id).toString() : Guid.EMPTY.toString(),
      type: s?.type,
      amount: typeof s?.amount === 'number' ? s.amount : Number(s?.amount ?? 0)
    }));

    const bp = new BudgetPlan();
    bp.id = raw?.id ? Guid.parse(raw.id).toString() : Guid.EMPTY.toString();
    bp.budgetItems = [...budgetItemsDirect, ...budgetItemsIndirect];
    bp.fundingSources = fundingSources;
    return bp;
  }

  completePreliminaryStep(): void {
    this.preliminaryStepCompleted = true;
  }

  private shouldSyncFinancialFields(): boolean {
    try {
      const budgetPlan = BudgetPlan.fromFormGroup(this.budgetPlanFormGroup);
      const currentAmountRequested = this.activityInfoGroup.get('amountRequestedFromMunicipality')?.value || 0;
      
      return Math.abs(budgetPlan.totalCostsRequested - currentAmountRequested) > 0.01;
    } catch {
      return false;
    } 
}

private syncFinancialFieldsFromBudgetPlan(): void {
  try {
    const budgetPlan = BudgetPlan.fromFormGroup(this.budgetPlanFormGroup);
    
    this.activityInfoGroup.patchValue({
      amountRequestedFromMunicipality: budgetPlan.totalCostsRequested
    }, { emitEvent: false });

  } catch (error) {
    console.warn('Financial fields snyc from budget plan failed:', error);
  }
}

  
onSubmit(stepper: MatStepper) {
  if (this.isUpdating) {
    this.updateAllocation(stepper);
  } else {
    this.createAllocation(stepper);
  }
}

createAllocation(stepper: MatStepper) {
    this.performSaveWithSync(() => this.performCreateAllocation(stepper));
}

updateAllocation(stepper: MatStepper) {
  this.performSaveWithSync(() => this.performUpdateAllocation(stepper));
}

saveAllocation() {
  this.performSaveWithSync(() => this.performSaveAllocation());
}

  private performSaveWithSync(saveAction: () => void): void {
  const needsSync = this.shouldSyncFinancialFields();
  
  if (!needsSync) {
    saveAction();
    return;
  }
  
  // Sync is mandatory - do it first
  this.syncFinancialFieldsFromBudgetPlan();
  
  // Show informational dialog
  this.dialog.open(GenericDialogComponent, {
   data: {
    title: 'Financijski podaci ažurirani',
    message: `Sustav je iz vašeg proračuna (obrazac B2) automatski ažurirao polje.

Na koraku "II. Podaci o aktivnosti", ažurirano je polje:

- "Zatraženi iznos"`,
    confirmButtonText: 'Razumijem',
    // No cancel button - only acknowledgment
  }
  }).afterClosed().pipe(
    takeUntil(this.destroy$)
  ).subscribe(() => {
    saveAction();
  });
}

  performCreateAllocation(stepper: MatStepper): void {
    if (!this.user?.associationId) {
      console.error('Association ID is missing');
      return;
    }

    const createAllocation: CreateAllocation = {
      ...this.generalInfoFormGroup.value,
      ...this.activityInfoGroup.value,
      ...this.justificationFormGroup.value,
      ...this.attachmentsFormGroup.value,
      budgetPlan: this.buildBudgetPlan(),
      associationId: this.user?.associationId

    };

    this.allocationService.createAllocation(createAllocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Zahtjev za izravnom dodjelom sredstava uspješno kreiran', 'Zatvori', this.snackbarConfig);
        stepper.reset();
        this.router.navigate(['association', 'allocations']);
      },
      error: (error) => {
        this.snackBar.open('Greška prilikom kreiranja zahtjeva za izravnom dodjelom sredstava', 'Zatvori', this.snackbarConfig);
        console.error(error);
      }
    });
  }

  performUpdateAllocation(stepper: MatStepper): void {

    const updateAllocation: UpdateAllocation = {
      id: this.allocationId,
      ...this.generalInfoFormGroup.value,
      ...this.activityInfoGroup.value,
      ...this.justificationFormGroup.value,
      ...this.attachmentsFormGroup.value,
      budgetPlan: this.buildBudgetPlan()
    };

    this.allocationService.updateAllocation(updateAllocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Zahtjev za izravnom dodjelom sredstava uspješno ažuriran', 'Zatvori', this.snackbarConfig);
        stepper.reset();
        this.router.navigate(['association', 'allocations']);
      },
      error: (error) => {
        this.snackBar.open('Greška prilikom ažuriranja zahtjeva za izravnom dodjelom sredstava', 'Zatvori', this.snackbarConfig);
        console.error(error);
      }
    });
  }

  performSaveAllocation(): void {
      if (this.isSaving) {
         return;
      }
  
      this.isSaving = true;
  
      const formsToValidate = [
        this.generalInfoFormGroup,
        this.activityInfoGroup,
        this.justificationFormGroup,
        this.budgetPlanFormGroup,
        this.attachmentsFormGroup
      ];
  
      const invalidForms = formsToValidate.filter(form => form && form.invalid);
      
      if (invalidForms.length > 0) {
        // Mark all fields as touched to show validation errors
        formsToValidate.forEach(form => form?.markAllAsTouched());
        
        this.snackBar.open('Molimo ispravno ispuniti sva obavezna polja prije spremanja', 'Zatvori', this.snackbarConfig);
        this.isSaving = false;
        return;
      }
  
      if (this.isUpdating) {
        // Edit mode - call update endpoint
          const updateAllocation: UpdateAllocation = {
            id: this.allocationId,
            ...this.generalInfoFormGroup.value,
            ...this.activityInfoGroup.value,
            ...this.justificationFormGroup.value,
            ...this.attachmentsFormGroup.value,
            budgetPlan: this.buildBudgetPlan()
          };

       this.allocationService.updateAllocation(updateAllocation).pipe(
          switchMap(() => this.loadAllocationData$()),
          finalize(() => this.isSaving = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.snackBar.open('Zahtjev za izravnom dodjelom sredstava uspješno ažuriran', 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            this.snackBar.open('Greška prilikom ažuriranja zahtjeva za izravnom dodjelom sredstava', 'Zatvori', this.snackbarConfig);
            console.error(error);
          }
        });
  
      } else {
    
         const createAllocation: CreateAllocation = {
          ...this.generalInfoFormGroup.value,
          ...this.activityInfoGroup.value,
          ...this.justificationFormGroup.value,
          ...this.attachmentsFormGroup.value,
          budgetPlan: this.buildBudgetPlan(),
          associationId: this.user?.associationId
        };

         this.allocationService.createAllocation(createAllocation).pipe(
          tap(allocationId => {
            // Transform to edit mode seamlessly
            this.allocationId = allocationId;
            this.isUpdating = true;
          }),
          switchMap(() => this.loadAllocationData$()),
          finalize(() => this.isSaving = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            // Update URL to edit mode
            const newUrl = this.router.createUrlTree(['association', 'allocations', this.allocationId, 'edit']);
            this.location.replaceState(newUrl.toString());
  
            this.snackBar.open('Zahtjev za izravnom dodjelom sredstava uspješno kreiran', 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            this.snackBar.open('Greška prilikom kreiranja zahtjeva za izravnom dodjelom sredstava ', 'Zatvori', this.snackbarConfig);
            console.error(error);
          }
        });

      }
  }
}
